'use client'

import { useEffect, useCallback } from 'react'
import { X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { CustomHeaderField } from '@/types/theme'

interface MobileNavDrawerProps {
  isOpen: boolean
  onClose: () => void
  headerFields: CustomHeaderField[]
}

const isTrueFlag = (value: unknown): boolean => value === true || value === 'true'

export function MobileNavDrawer({
  isOpen,
  onClose,
  headerFields
}: MobileNavDrawerProps) {
  // Handle escape key
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
      document.body.style.overflow = 'hidden'
      document.body.style.paddingRight = `${scrollbarWidth}px`
    } else {
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
    }

    return () => {
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
    }
  }, [isOpen])

  const handleBackdropClick = useCallback(() => {
    onClose()
  }, [onClose])

  const handleLinkClick = useCallback(() => {
    // Fecha o menu ao clicar em um link (melhor UX)
    onClose()
  }, [onClose])

  if (headerFields.length === 0) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop/Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
            onClick={handleBackdropClick}
            aria-hidden="true"
          />

          {/* Drawer lateral */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 z-[70] w-[85%] max-w-sm shadow-2xl"
            style={{
              backgroundColor: 'var(--login-background,rgb(29, 29, 29))',
              borderLeft: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            {/* Header do drawer */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2
                className="text-lg font-semibold"
                style={{ color: 'var(--login-header-text, #ffffff)' }}
              >
                Menu
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-full transition-colors hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20"
                aria-label="Fechar menu"
              >
                <X className="w-5 h-5" style={{ color: 'var(--login-header-text, #ffffff)' }} />
              </button>
            </div>

            {/* Lista de links */}
            <nav className="p-4">
              <ul className="space-y-2">
                {headerFields
                  .filter((field) => !isTrueFlag(field.only_members)) // Filtra apenas não-membros
                  .map((field, index) => (
                    <li key={`${field.id}-${index}`}>
                      <a
                        href={field.url}
                        target={field.url.startsWith('http') ? '_blank' : undefined}
                        rel={field.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                        onClick={handleLinkClick}
                        className="block px-4 py-3 rounded-lg font-medium transition-all hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20"
                        style={{ color: 'var(--login-header-text, #ffffff)' }}
                      >
                        <span className="flex items-center justify-between">
                          {field.label}
                          {field.url.startsWith('http') && (
                            <svg
                              className="w-4 h-4 opacity-60"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                          )}
                        </span>
                      </a>
                    </li>
                  ))}
              </ul>
            </nav>

            {/* Footer opcional com gradiente para visual mais clean */}
            <div
              className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none"
              style={{
                background: 'linear-gradient(to top, var(--login-background, rgb(21, 21, 21)), transparent)'
              }}
            />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

